/**
 * Centralised React Query key factory. Keeping every key in one place makes
 * cache invalidation predictable and prevents typo-driven cache misses.
 */
export const queryKeys = {
  auth: {
    me: ["auth", "me"] as const,
  },
  users: {
    all: ["users"] as const,
    list: (params?: unknown) => ["users", "list", params] as const,
    detail: (id: string) => ["users", "detail", id] as const,
  },
  clients: {
    all: ["clients"] as const,
    list: (params?: unknown) => ["clients", "list", params] as const,
    detail: (id: string) => ["clients", "detail", id] as const,
    myProfile: ["clients", "me"] as const,
  },
  staff: {
    all: ["staff"] as const,
    list: (params?: unknown) => ["staff", "list", params] as const,
    detail: (id: string) => ["staff", "detail", id] as const,
  },
  salaries: {
    all: ["salaries"] as const,
    list: (params?: unknown) => ["salaries", "list", params] as const,
    summary: (month?: string) => ["salaries", "summary", month] as const,
    transactions: (params?: unknown) => ["salaries", "transactions", params] as const,
    ledgerSummary: (staffId?: string) => ["salaries", "ledgerSummary", staffId] as const,
  },
  categories: {
    all: ["categories"] as const,
    list: ["categories", "list"] as const,
    detail: (id: string) => ["categories", "detail", id] as const,
  },
  products: {
    all: ["products"] as const,
    list: (params?: unknown) => ["products", "list", params] as const,
    detail: (id: string) => ["products", "detail", id] as const,
  },
  inventory: {
    all: ["inventory"] as const,
    stock: (params?: unknown) => ["inventory", "stock", params] as const,
    valuation: ["inventory", "valuation"] as const,
    lowStock: ["inventory", "low-stock"] as const,
    movements: (params?: unknown) => ["inventory", "movements", params] as const,
    ledger: (id: string, params?: unknown) =>
      ["inventory", "ledger", id, params] as const,
  },
  production: {
    all: ["production"] as const,
    machines: ["production", "machines"] as const,
    runs: (params?: unknown) => ["production", "runs", params] as const,
    run: (id: string) => ["production", "run", id] as const,
  },
  orders: {
    all: ["orders"] as const,
    list: (params?: unknown) => ["orders", "list", params] as const,
    detail: (id: string) => ["orders", "detail", id] as const,
  },
  payments: {
    all: ["payments"] as const,
    list: (params?: unknown) => ["payments", "list", params] as const,
    receivables: ["payments", "receivables"] as const,
    clientLedger: (id: string) => ["payments", "ledger", id] as const,
    myLedger: ["payments", "ledger", "me"] as const,
    receipt: (id: string) => ["payments", "receipt", id] as const,
  },
  notifications: {
    all: ["notifications"] as const,
    list: (params?: unknown) => ["notifications", "list", params] as const,
    unreadCount: ["notifications", "unread-count"] as const,
  },
  dashboard: {
    overview: ["dashboard", "overview"] as const,
  },
  reports: {
    sales: (params?: unknown) => ["reports", "sales", params] as const,
    production: (params?: unknown) => ["reports", "production", params] as const,
    daily: (params?: unknown) => ["reports", "daily", params] as const,
  },
} as const;
